"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin, Navigation, CircleCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const communities = ["Pittsburgh", "Plum", "Murrysville", "Penn Hills", "Monroeville", "Oakmont", "Fox Chapel", "New Kensington", "Apollo", "Vandergrift", "Lower Burrell", "Springdale"];
export default function OurServiceArea() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: mapRef,
    isVisible: isMapVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="our-service-area" className="py-16 md:py-24 bg-background overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {}
          <div ref={textRef} className={`flex flex-col space-y-8 transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                <Navigation className="size-4" />
                <span>Local Experts</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-foreground tracking-tight">
                Proudly Serving Pittsburgh & Surrounding Areas
              </h2>
              <p className="text-lg text-muted-foreground max-w-xl leading-relaxed">
                For over 40 years, Nieser Landscaping has been the trusted choice for homeowners across the region. We bring our decades of experience and dedication to quality craftsmanship directly to your neighborhood.
              </p>
            </div>

            <div className="flex items-start gap-4 p-6 rounded-2xl bg-card border border-border shadow-sm">
              <div className="bg-primary/10 p-3 rounded-full shrink-0">
                <MapPin className="size-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-card-foreground text-lg mb-1">Our Headquarters</h3>
                <p className="text-muted-foreground">2746 Leechburg Rd</p>
                <p className="text-muted-foreground">Pittsburgh, PA 15239</p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-foreground text-lg flex items-center gap-2">
                <CircleCheck className="size-5 text-primary" />
                Nearby Communities We Serve
              </h3>
              <div className="flex flex-wrap gap-2">
                {communities.map((community, i) => <Badge key={i} data-index={i} variant="secondary" className="px-3 py-1.5 text-sm bg-muted hover:bg-primary hover:text-primary-foreground transition-colors cursor-default">
                    {community}
                  </Badge>)}
              </div>
            </div>

            <div className="pt-4">
              <Button asChild size="lg" className="w-full sm:w-auto text-base h-12 px-8">
                <Link to="/GetAnEstimatePage">
                  Request a Free Estimate
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={mapRef} className={`relative h-[500px] lg:h-[650px] rounded-3xl overflow-hidden shadow-2xl border border-border/50 transition-all duration-1000 delay-200 ease-out ${isMapVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            {}
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586632/img/pittsburgh_region_service_map.png" alt="Map showing Nieser Landscaping service area in Pittsburgh" className="absolute inset-0 w-full h-full object-cover object-center" />
            
            {}
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />

            {}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center animate-bounce" style={{
            animationDuration: '3s'
          }}>
              <div className="relative">
                <div className="absolute inset-0 bg-primary/30 rounded-full animate-ping" />
                <div className="relative bg-primary text-primary-foreground p-4 rounded-full shadow-xl border-4 border-background">
                  <MapPin className="size-8" />
                </div>
              </div>
              <div className="mt-3 bg-background/95 backdrop-blur-sm px-5 py-2.5 rounded-lg shadow-lg border border-border text-center">
                <p className="font-bold text-foreground text-sm">Nieser Landscaping</p>
                <p className="text-xs text-muted-foreground mt-0.5">Pittsburgh, PA</p>
              </div>
            </div>

            {}
            <div className="absolute bottom-6 left-6 right-6 bg-background/90 backdrop-blur-md p-4 rounded-xl border border-border shadow-lg flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="size-3 rounded-full bg-primary animate-pulse" />
                <span className="text-sm font-medium text-foreground">Active Service Area</span>
              </div>
              <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Est. 1984</span>
            </div>
          </div>

        </div>
      </div>
    </section>;
}